import { Transcript } from 'src/models/Transcript'
import { BaseTranscriptClient, type TranscriptClient } from 'src/models/TranscriptClient'
import { TranscriptOld } from 'src/models/TranscriptOld'
import { Language } from 'src/models/Language'
import { TranscriptEvent } from 'src/models/TranscriptEvent'
import { DummyTranscriptUserStateManager } from 'src/models/TranscriptUserState'
import { TranscriptConnectionStateManager, DEFAULT_TRANSCRIPT_CONNECTION_STATE } from 'src/models/TranscriptConnectionState'
import { DrainOutcome } from 'src/models/TranscriptOld'
import { debug } from 'src/services/DebugService'

import { ASRUpdateStreamMessage } from './StreamClient'

type AblyTranscriptClientOptions = {
    requestedLanguage: Language
}

export class AblyTranscriptClient extends BaseTranscriptClient {
    readonly source = 'ably' as const
    private _transcriptOld: TranscriptOld = TranscriptOld.EMPTY
    private _requestedLanguage: Language
    private _userStateManager = new DummyTranscriptUserStateManager()
    private _connectionStateManager = new TranscriptConnectionStateManager(DEFAULT_TRANSCRIPT_CONNECTION_STATE)

    constructor({ requestedLanguage }: AblyTranscriptClientOptions) {
        super()

        this._requestedLanguage = requestedLanguage
    }

    init = async () => {}
    destroy = () => {}
    getUserState = () => this._userStateManager
    getConnectionState = () => this._connectionStateManager

    /**
     * BaseTranscriptClient relies on this._transcript for all the query methods.
     * Since Ably works with the old version of the transcript we make sure we update both.
     */
    private _setTranscript = (transcriptOld: TranscriptOld) => {
        this._transcriptOld = transcriptOld
        this._transcript = Transcript.fromOldStructure(transcriptOld, this._requestedLanguage)
    }

    processAsrHistory = (initialTranscript: TranscriptOld, history: ASRUpdateStreamMessage[]) => {
        this._setTranscript(TranscriptOld.mergeAsrTranscriptionHistory(initialTranscript, history))

        const events: TranscriptEvent[] = [
            {
                type: 'map',
                isLocal: false,
                tags: [],
                path: [],
                delta: [{ change: ['paragraphIds', 'paragraphs', 'speakers'] }],
            },
            {
                type: 'array',
                isLocal: false,
                tags: [],
                path: ['paragraphIds'],
                delta: [{ insert: this.getParagraphIds() }],
            },
        ]

        debug.log('AblyTranscriptClient processAsrHistory', this._transcript, events)

        // emit initial events to make sure the UI is updated.
        this.emitEvents(events)
        this._connectionStateManager.setConnectionState({ isInitialized: true })
    }

    processAsrQueue = (queue: ASRUpdateStreamMessage[]): DrainOutcome => {
        const addedParagraphIds = new Set<string>()
        const changedParagraphIds = new Set<string>()
        const currentParagraphIds = this.getParagraphIds()

        let preserveDiff = false

        // we need to figure out what actually changes with the queued messages.
        // Since there is no editing with Ably, we can limit the diff check to only paragraphs.
        for (const msg of queue) {
            for (const word of msg.data.word_list) {
                if (word.paragraph_id) {
                    if (currentParagraphIds.includes(word.paragraph_id)) {
                        changedParagraphIds.add(word.paragraph_id)
                    } else {
                        addedParagraphIds.add(word.paragraph_id)
                    }
                }
            }

            this._setTranscript(TranscriptOld.mergeAsrChunk(this._transcriptOld, msg.data, preserveDiff))
            preserveDiff = true
        }

        const events: TranscriptEvent[] = []

        if (addedParagraphIds.size > 0) {
            events.push({
                type: 'array',
                isLocal: false,
                tags: [],
                path: ['paragraphIds'],
                // Sam: this delta is maybe not accurate, need to revisit if this delta becomes important or a problem.
                delta: [{ retain: currentParagraphIds.length - 1 }, { insert: Array.from(addedParagraphIds) }],
            })
        }

        if (changedParagraphIds.size > 0) {
            events.push({
                type: 'map',
                isLocal: false,
                tags: [],
                path: ['paragraphs'],
                delta: [{ change: Array.from(changedParagraphIds) }],
            })
            for (const id of changedParagraphIds) {
                events.push({
                    type: 'map',
                    isLocal: false,
                    tags: [],
                    path: ['paragraphs', id],
                    // we don't know which keys changed so we pass an empty delta. Might have to change if this becomes a problem.
                    delta: [{ change: [] }],
                })
            }
        }

        debug.log('AblyTranscriptClient processAsrQueue', this._transcript, events)

        this.emitEvents(events)

        return { drained: queue.length, added: addedParagraphIds.size, changed: changedParagraphIds.size }
    }

    applyOps: TranscriptClient['applyOps'] = () => {
        throw new Error('AblyTranscriptClient does not support modifications. This is a logic error.')
    }

    // No write path. Ignore the editor write guard and leave isReadOnly true.
    setReadOnly = (isReadOnly: boolean) => {
        void isReadOnly
    }

    undo = () => {
        // Ably doesn't support undo/redo. So we do nothing
    }

    redo = () => {
        // Ably doesn't support undo/redo. So we do nothing
    }
}
